import React, { useState } from 'react';
import {
  Upload, ArrowRight, Check, Sparkles, AlertCircle, Users, CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const STEPS = ['Upload', 'Map Columns', 'Confirm'];

const CSV_COLUMNS = [
  'Company Name', 'Address', 'Category', 'Phone', 'Trigger Event', 'Date Detected', 'Priority Score', 'Notes',
];
const INTERNAL_FIELDS = [
  'Business Name', 'Address', 'Category', 'Phone', 'Trigger Event', 'Trigger Date', '— skip —', 'Notes',
];

const PREVIEW_ROWS = [
  { business: 'KFire Korean BBQ - Logan Square', category: 'Korean Restaurant', trigger: 'New radio campaign on WBBM-FM' },
  { business: 'Audi Denver', category: 'Car Dealership', trigger: 'Increased radio spend detected' },
  { business: 'Twin Aspen Dental Center', category: 'Dental Practice', trigger: 'First-time radio advertiser' },
];

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center gap-2 px-6 py-3 border-b border-border">
    {STEPS.map((label, i) => (
      <React.Fragment key={label}>
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors',
              i < currentStep
                ? 'bg-teal text-white'
                : i === currentStep
                  ? 'bg-gradient-to-br from-coral to-teal text-white'
                  : 'bg-muted text-muted-foreground'
            )}
          >
            {i < currentStep ? <Check className="h-3 w-3" /> : i + 1}
          </div>
          <span
            className={cn(
              'text-sm',
              i === currentStep ? 'font-semibold text-foreground' : 'text-muted-foreground'
            )}
          >
            {label}
          </span>
        </div>
        {i < STEPS.length - 1 && (
          <div className={cn('flex-1 h-px', i < currentStep ? 'bg-teal' : 'bg-border')} />
        )}
      </React.Fragment>
    ))}
  </div>
);

const UploadStep = ({ onNext }) => {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Upload CSV file — drop here or click to browse"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); onNext(); }}
      onClick={onNext}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNext(); } }}
      className={cn(
        'border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all',
        dragOver
          ? 'border-teal bg-teal/5'
          : 'border-border hover:border-muted-foreground/30 hover:bg-muted/30'
      )}
    >
      <Upload className={cn('h-10 w-10 mx-auto mb-3', dragOver ? 'text-teal' : 'text-muted-foreground')} />
      <p className="text-sm font-semibold text-foreground mb-1">
        Drop your CSV here, or click to browse
      </p>
      <p className="text-xs text-muted-foreground">Supports .csv and .tsv files up to 10MB</p>
    </div>
  );
};

const MappingStep = () => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 bg-emerald-50/50 border border-emerald-100 rounded-lg px-3 py-2">
      <Sparkles className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
      <span className="text-xs text-emerald-700 font-medium">
        Auto-detected 8 columns from <strong>iheart_leads_feb2026.csv</strong>
      </span>
    </div>

    <div className="grid grid-cols-[1fr_24px_1fr] gap-x-3 gap-y-2 items-center">
      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">CSV Column</span>
      <span />
      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">CornerCast Field</span>

      {CSV_COLUMNS.map((col, i) => {
        const isSkipped = INTERNAL_FIELDS[i] === '— skip —';
        return (
          <React.Fragment key={col}>
            <div className="bg-muted rounded-md px-3 py-2 text-xs font-mono text-foreground truncate">
              {col}
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground mx-auto" />
            <div
              className={cn(
                'rounded-md px-3 py-2 text-xs font-medium border truncate',
                isSkipped
                  ? 'bg-amber-50/50 text-amber-600 border-amber-100'
                  : 'bg-teal/5 text-teal border-teal/20'
              )}
            >
              {INTERNAL_FIELDS[i]}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

const ConfirmStep = () => (
  <div className="space-y-4">
    <div className="bg-muted/50 rounded-lg p-4">
      <div className="grid grid-cols-3 gap-4 text-center">
        {[
          { label: 'Total leads', value: '47', Icon: Users },
          { label: 'Ready to process', value: '43', Icon: CheckCircle2 },
          { label: 'Skipped (incomplete)', value: '4', Icon: AlertCircle },
        ].map(({ label, value, Icon }) => (
          <div key={label}>
            <Icon className="h-5 w-5 text-muted-foreground mx-auto mb-1" />
            <div className="text-2xl font-bold text-foreground">{value}</div>
            <div className="text-xs text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>
    </div>

    <div className="rounded-md border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-[10px] uppercase tracking-wider">Business</TableHead>
            <TableHead className="text-[10px] uppercase tracking-wider">Category</TableHead>
            <TableHead className="text-[10px] uppercase tracking-wider">Trigger Event</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {PREVIEW_ROWS.map((row) => (
            <TableRow key={row.business}>
              <TableCell className="font-medium text-sm">{row.business}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{row.category}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{row.trigger}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={3} className="text-center text-xs text-muted-foreground">
              + 40 more leads
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  </div>
);

const CSVImportDialog = ({ open, onClose }) => {
  const [step, setStep] = useState(0);

  const handleClose = () => {
    setStep(0);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0">
        <DialogHeader className="p-6 pb-3">
          <DialogTitle>Import Leads</DialogTitle>
          <DialogDescription>
            {step === 0 && 'Upload a CSV file from your media monitor'}
            {step === 1 && 'Map columns to CornerCast fields'}
            {step === 2 && 'Review and confirm import'}
          </DialogDescription>
        </DialogHeader>

        <StepIndicator currentStep={step} />

        <div className="px-6 py-4 max-h-[50vh] overflow-y-auto">
          {step === 0 && <UploadStep onNext={() => setStep(1)} />}
          {step === 1 && <MappingStep />}
          {step === 2 && <ConfirmStep />}
        </div>

        <DialogFooter className="p-6 pt-4 border-t border-border">
          <Button variant="outline" onClick={step === 0 ? handleClose : () => setStep(step - 1)}>
            {step === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Button onClick={step === 2 ? handleClose : () => setStep(step + 1)}>
            {step === 0 ? 'Continue' : step === 1 ? 'Looks Good' : 'Import 43 Leads'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CSVImportDialog;
